import * as fs from "node:fs";
import { LocalCliError } from "./errors.js";

/**
 * Render a persona's body (everything after the closing frontmatter `---`)
 * by substituting `{{field-name}}` placeholders with values from the map.
 *
 * Used by the brain-link / brain-unlink cascades to re-render the body
 * deterministically without re-prompting the operator (values are persisted
 * in ~/.m8t/foundry/<agent>.yaml under `fillableFieldValues`).
 *
 * Mirrors the substitution semantics of `targets/foundry/README.md` step 5:
 *   - Simple regex string-replace (no template engine).
 *   - Defensive: after substitution, scans for residual {{...}}; if any
 *     remain, throws — the persona references fields not in the map.
 *
 * @throws LocalCliError when the file is unreadable or placeholders are unsatisfied.
 */
export function renderPersonaBody(
  personaPath: string,
  fillableFieldValues: Record<string, string>,
  instanceName?: string,
): string {
  let raw: string;
  try {
    raw = fs.readFileSync(personaPath, "utf8");
  } catch (e) {
    throw new LocalCliError({
      code: "PERSONA_READ_FAILED",
      message: `Could not read persona file '${personaPath}': ${(e as Error).message}`,
    });
  }

  // Strip frontmatter: anything between the first `---` and the next `---`.
  // \r?\n tolerates CRLF line endings (Windows git checkouts) as well as LF.
  const fmMatch = /^---\r?\n[\s\S]*?\r?\n---(?:\r?\n)+/.exec(raw);
  const body = fmMatch ? raw.slice(fmMatch[0].length) : raw;

  // Substitute placeholders.
  let rendered = body;
  for (const [name, value] of Object.entries(fillableFieldValues)) {
    rendered = rendered.replaceAll(`{{${name}}}`, value);
  }

  // Defensive scan: any residual {{...}} ?
  const residual = rendered.match(/\{\{([a-zA-Z][\w-]*)\}\}/g);
  if (residual && residual.length > 0) {
    const names = [...new Set(residual.map((s) => s.slice(2, -2)))];
    throw new LocalCliError({
      code: "PERSONA_PLACEHOLDER_UNSATISFIED",
      message: `Persona '${personaPath}' has un-substituted placeholders: ${names.join(", ")}`,
      hint: `Add these to fillableFieldValues in ~/.m8t/foundry/<agent>.yaml, or re-deploy the agent via the architect to repopulate.`,
    });
  }

  const out = rendered.replace(/\s+$/, ""); // strip trailing whitespace; preserve internal layout
  return instanceName ? injectInstanceIdentity(out, instanceName) : out;
}

/**
 * Inject the deploy-time instance identity so a type-keyed persona (e.g. `ezra`)
 * self-IDs by its deployed NAME (e.g. Ezra). The persona is the reusable TYPE; the deployed
 * name is a deploy attribute — this is the OOP primitive that was otherwise hand-baked into
 * the live agent and lost on every brain-link re-render. Placed right after the leading H1
 * title when present, else prepended. No-op when instanceName is empty.
 */
export function injectInstanceIdentity(body: string, instanceName: string): string {
  const display = instanceName.charAt(0).toUpperCase() + instanceName.slice(1);
  const line = `**You are ${display}.** Introduce yourself as ${display} on first contact.`;
  const title = /^(#[^\n]*\n)/.exec(body);
  return title
    ? `${title[0]}\n${line}\n${body.slice(title[0].length)}`
    : `${line}\n\n${body}`;
}
